import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Eye, Users, Gift, TrendingUp, Building, Calendar, Bookmark } from 'lucide-react';

export default function InsightsPage() {
  const { user, axiosAuth } = useAuth();
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const api = axiosAuth();

  useEffect(() => {
    api.get('/insights/resource-partner').then(r => {
      setInsights(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-3 border-emerald-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!insights || insights.error) {
    return (
      <div className="space-y-6" data-testid="insights-page">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>Insights</h1>
          <p className="text-muted-foreground text-sm mt-1">Track your community impact</p>
        </div>
        <Card className="border-2 border-dashed rounded-2xl">
          <CardContent className="py-12 text-center">
            <Building className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-muted-foreground">Create your organization profile to start tracking insights.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const totalEngagement = (insights.total_views || 0) + (insights.total_interests || 0) + (insights.total_referrals || 0);
  const opportunities = insights.recent_opportunities || [];

  return (
    <div className="space-y-6" data-testid="insights-page">
      <div>
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>Insights & Analytics</h1>
        <p className="text-muted-foreground text-sm mt-1">Track your organization's community impact</p>
      </div>

      {/* Impact Summary */}
      <Card className="border-0 shadow-sm rounded-2xl bg-gradient-to-br from-emerald-50 to-teal-50">
        <CardContent className="p-6">
          <div className="text-center">
            <p className="text-5xl font-bold text-emerald-700">{totalEngagement}</p>
            <p className="text-emerald-600 font-medium mt-1">Total Community Engagements</p>
            <p className="text-sm text-emerald-600/70 mt-2">
              Your opportunities are making a difference!
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm rounded-2xl">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
                <Gift className="w-4 h-4 text-emerald-600" />
              </div>
            </div>
            <p className="text-2xl font-bold">{insights.total_opportunities || 0}</p>
            <p className="text-xs text-muted-foreground">Total Opportunities</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm rounded-2xl">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-sky-50 flex items-center justify-center">
                <Eye className="w-4 h-4 text-sky-600" />
              </div>
            </div>
            <p className="text-2xl font-bold">{insights.total_views || 0}</p>
            <p className="text-xs text-muted-foreground">Total Views</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm rounded-2xl">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
                <Bookmark className="w-4 h-4 text-amber-600" />
              </div>
            </div>
            <p className="text-2xl font-bold">{insights.total_interests || 0}</p>
            <p className="text-xs text-muted-foreground">Saved / Interested</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm rounded-2xl">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center">
                <Users className="w-4 h-4 text-violet-600" />
              </div>
            </div>
            <p className="text-2xl font-bold">{insights.total_referrals || 0}</p>
            <p className="text-xs text-muted-foreground">Referrals</p>
          </CardContent>
        </Card>
      </div>

      {/* Opportunity Performance */}
      {opportunities.length > 0 && (
        <Card className="border-0 shadow-sm rounded-2xl">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope' }}>
              Opportunity Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {opportunities.map(opp => {
                const maxEngagement = Math.max(...opportunities.map(o => (o.views || 0) + (o.interests || 0)), 1);
                const engagement = (opp.views || 0) + (opp.interests || 0);
                const percent = (engagement / maxEngagement) * 100;
                
                return (
                  <div key={opp.id} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{opp.title}</p>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                          <span className="flex items-center gap-1">
                            <Eye size={10} /> {opp.views || 0}
                          </span>
                          <span className="flex items-center gap-1">
                            <Bookmark size={10} /> {opp.interests || 0}
                          </span>
                          <Badge className={opp.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'} variant="secondary">
                            {opp.status}
                          </Badge>
                        </div>
                      </div>
                      <span className="text-sm font-medium text-muted-foreground ml-4">
                        {engagement}
                      </span>
                    </div>
                    <Progress value={percent} className="h-2" />
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Organization Info */}
      {insights.organization && (
        <Card className="border-0 shadow-sm rounded-2xl">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope' }}>
              Organization Profile
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
                <Building className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="font-semibold">{insights.organization.name}</p>
                <Badge className="bg-emerald-50 text-emerald-700 mt-1">{insights.organization.category}</Badge>
                {insights.organization.service_area && (
                  <p className="text-sm text-muted-foreground mt-2">
                    Service Area: {insights.organization.service_area}
                  </p>
                )}
                {insights.organization.verified && (
                  <p className="text-xs text-emerald-600 font-medium mt-2">Verified Organization</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
